import { Check } from "lucide-react";

const TEMPLATE_PREVIEWS = {
  modern: {
    description: "Sans-serif, single column, blue accent. Versatile default.",
    spec: "Helvetica · 1-col · 100% density",
  },
  classic: {
    description: "Serif typography, navy accent. Traditional / corporate.",
    spec: "Garamond/Times · 1-col · 105% density",
  },
  compact: {
    description: "Dense layout, smaller text. Fits long resumes on one page.",
    spec: "Helvetica · 1-col · 75% density",
  },
  two_column: {
    description: "Sidebar with skills/edu, main column for experience.",
    spec: "Helvetica · 2-col · 95% density",
  },
};

function MiniPreview({ tplKey, accent }) {
  const accentColor = `#${accent}`;
  const lines = (count, w = "70%") =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={`${tplKey}-line-${i}-${w}`}
        className="h-[3px] bg-[var(--bg-muted)]"
        style={{ width: w }}
      />
    ));

  if (tplKey === "two_column") {
    return (
      <div className="w-full h-24 bg-white border border-[var(--border-default)] p-2 grid grid-cols-[1fr_2fr] gap-2">
        <div className="space-y-1">
          <div className="h-[5px]" style={{ background: accentColor, width: "60%" }} />
          {lines(3, "85%")}
        </div>
        <div className="space-y-1">
          <div className="h-[6px]" style={{ background: accentColor, width: "45%" }} />
          {lines(4, "95%")}
        </div>
      </div>
    );
  }

  if (tplKey === "compact") {
    return (
      <div className="w-full h-24 bg-white border border-[var(--border-default)] p-2 space-y-[3px]">
        <div className="h-[5px]" style={{ background: accentColor, width: "35%" }} />
        {lines(7, "95%")}
      </div>
    );
  }

  if (tplKey === "classic") {
    return (
      <div className="w-full h-24 bg-white border border-[var(--border-default)] p-2 space-y-1">
        <div className="h-[6px] bg-[var(--text-primary)]" style={{ width: "55%" }} />
        <div className="h-[3px] bg-[var(--bg-muted)]" style={{ width: "30%" }} />
        <div className="border-t border-[var(--border-default)] my-1" />
        <div className="h-[4px]" style={{ background: accentColor, width: "20%" }} />
        {lines(3, "95%")}
      </div>
    );
  }

  // modern
  return (
    <div className="w-full h-24 bg-white border border-[var(--border-default)] p-2 space-y-1">
      <div className="h-[7px] bg-[var(--text-primary)]" style={{ width: "60%" }} />
      <div className="h-[3px]" style={{ background: accentColor, width: "35%" }} />
      <div className="h-[4px]" style={{ background: accentColor, width: "18%" }} />
      {lines(4, "95%")}
    </div>
  );
}

export default function TemplatePicker({ templates, selected, onChange, label = "TEMPLATE" }) {
  if (!templates?.length) return null;
  return (
    <div className="space-y-3" data-testid="template-picker">
      <div className="overline">{label}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {templates.map((t) => {
          const active = selected === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange(t.key)}
              data-testid={`template-option-${t.key}`}
              className={`text-left p-3 border transition-all ${
                active
                  ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5"
                  : "border-[var(--border-default)] hover:border-[var(--text-soft)]"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading font-bold text-sm tracking-tight">
                  {t.label}
                </span>
                {active && (
                  <Check
                    className="w-4 h-4 text-[var(--brand-primary)]"
                    strokeWidth={2}
                  />
                )}
              </div>
              <MiniPreview tplKey={t.key} accent={t.accent} />
              <p className="text-xs text-[var(--text-secondary)] mt-2 leading-snug">
                {TEMPLATE_PREVIEWS[t.key]?.description}
              </p>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--text-muted)] block mt-1">
                {TEMPLATE_PREVIEWS[t.key]?.spec}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
